import { identifyUser } from "@/api";
import { useUserStore } from "@/stores";
import { ChatbaseUrls } from "@/config";

// 插件端打开客服时带上的来源参数，见 ChatbaseUrls
const ChatbaseQueryKey = "chatbase";

// 页面初始加载时的来源参数（跳转登录页后 URL 会变，所以提前取）
const initialSource = new URLSearchParams(window.location.search).get(
  ChatbaseQueryKey,
);

const ChatbotId = "qVUWP0U--IEuioRn2wvmr";
// 等待身份接口的最长时间，超时则按匿名加载，避免客服按钮迟迟不出现
const IdentifyTimeout = 3000;

let identified = false;
let autoOpened = false;
let scriptLoaded = false;

function getChatbase(): any {
  return (window as any).chatbase;
}

/** 加载 Chatbase embed 脚本；加载前的 identify/open 调用会进入 index.html 中定义的队列，加载后按顺序执行 */
function loadChatbaseScript() {
  if (scriptLoaded) return;
  scriptLoaded = true;
  const script = document.createElement("script");
  script.src = "https://www.chatbase.co/embed.min.js";
  script.id = ChatbotId;
  (script as any).domain = "www.chatbase.co";
  document.body.appendChild(script);
}

/**
 * 网页端路由守卫中调用：已登录则先关联用户，再加载 Chatbase 脚本；
 * 从插件端跳转过来（带 ?chatbase=xxx）时自动打开客服窗口，未登录也能打开（匿名）
 */
export async function syncChatbase(logged: boolean) {
  if (!getChatbase()) return;
  if (logged) {
    // 先关联身份再加载脚本，保证客服窗口出现时已是关联状态
    await Promise.race([
      identifyChatbaseUser(),
      new Promise((resolve) => setTimeout(resolve, IdentifyTimeout)),
    ]);
  }
  loadChatbaseScript();
  if (initialSource && !autoOpened) {
    autoOpened = true;
    getChatbase().open();
  }
}

/** 把当前用户关联到 Chatbase（仅网页端加载了 embed 脚本时生效） */
async function identifyChatbaseUser() {
  const chatbase = getChatbase();
  if (!chatbase || identified) return;
  identified = true;
  try {
    const res = await identifyUser();
    const { userId, hashedId, token } = res?.data || {};
    const userStore = useUserStore();
    const userInfo: any = userStore.userInfo || {};
    const profile = userInfo.linkedinProfile;
    const name = profile
      ? [profile.firstName, profile.lastName].filter(Boolean).join(" ")
      : "";
    const attrs = {
      email: userInfo.email || "",
      name,
      ...(initialSource ? { source: initialSource } : {}),
    };
    if (token) {
      // JWT 方式：token 里的 email/name 等会写入 Chatbase Contact
      chatbase("identify", { token, ...attrs });
    } else if (userId && hashedId) {
      // user_hash 方式：Contact 只有 user_id，user_metadata 仅供对话上下文使用
      chatbase("identify", {
        user_id: userId,
        user_hash: hashedId,
        user_metadata: attrs,
      });
    } else {
      identified = false;
    }
  } catch (e) {
    identified = false;
    console.error("chatbase identify failed", e);
  }
}

/** 退出登录时清除 Chatbase 中的用户身份 */
export function resetChatbaseUser() {
  identified = false;
  getChatbase()?.("resetUser");
}

/** 打开客服：网页端直接打开 widget，插件端跳转到网页端带上来源参数 */
export function openChatbase(source: keyof typeof ChatbaseUrls) {
  const chatbase = getChatbase();
  if (chatbase && window.location.protocol !== "chrome-extension:") {
    chatbase.open();
    return;
  }
  window.open(ChatbaseUrls[source], "_blank");
}
